// Tech Debt Payback calculator: React UI on the HeroResultLayout template. The maths lives
// in ./model.js. The chance the fix pays back is the headline; the fix can be handed to
// Sprint Mix as an Improve item (src/lib/handBack.js).

import { useMemo, useState } from "react";
import { HeroResultLayout, HeroSection } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  Graph,
  MetricCard,
  MetricGrid,
  OpenInTool,
  RangeInput,
  ResultCard,
  SectionHead,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
  type Range,
} from "@/components/layout";
import { Input } from "@/components/ui/input";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { hubLink, hubState, initialBack } from "@/lib/handBack.js";
import { analyseDebt, fmt, pct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { techDebtShare } from "./share.js";
import { DEFAULT_STATE as MIX_DEFAULT, mixItem, sprintMixShare } from "@/sprintmix/share.js";

const HEADING = headingFor("tool/tech-debt-calculator");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface State {
  label: string;
  principal: Range;
  interest: Range;
  recovered: Range;
  horizonMonths: number;
  growthPct: number;
  replacePct: number;
}

const INITIAL = EXAMPLES[0].state as State;
const numberOr = (text: string, fallback: number) => (text.trim() === "" || !Number.isFinite(Number(text)) ? fallback : Number(text));

export function App() {
  const shared = useUrlRestore<State>(techDebtShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  const [back] = useState(() => initialBack(["sprintmix"]));
  useUrlSync(techDebtShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const r: Any = useMemo(() => analyseDebt(state), [state]);
  const ok = !r.issues.length;

  const hub: Any = hubState(back, sprintMixShare);
  const mixHref = (() => {
    if (!ok) return undefined;
    const base = hub ?? { ...MIX_DEFAULT, capacityUnit: "days" };
    if (base.capacityUnit !== "days") return undefined;
    const round = (x: number) => Math.round(x * 10) / 10;
    const item = mixItem({
      label: state.label || "Tech debt fix",
      kind: "improve",
      team: "build",
      value: { low: round(Math.max(0, r.saved.p5)), high: round(r.saved.p95) },
      valueIn: "days",
      size: { ...state.principal },
    });
    return hubLink("/tool/sprint-planner", sprintMixShare, { ...base, items: [...base.items, item].slice(-40) });
  })();

  return (
    <HeroResultLayout
      label="Unit 38 / Tech debt payback"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="Technical debt calculator"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      result={
        <ResultCard
          label="Chance it pays back"
          value={ok ? pct(r.chancePaysBack) : "—"}
          caption={ok ? `within ${state.horizonMonths} months · expected net ${fmt(r.meanNet)} team-days` : "Fix the inputs below"}
          fraction={ok ? r.chancePaysBack : 0}
        />
      }
      sticky={ok ? { label: "Pays back", value: pct(r.chancePaysBack), fraction: r.chancePaysBack } : undefined}
      footer={
        <BarFooter>
          <span>Principal and interest · simulated ranges</span>
          <span>{ok ? `Payback about ${fmt(r.payback.p50)} months` : "Inputs incomplete"}</span>
        </BarFooter>
      }
    >
      <HeroSection>
        <Explainer id="techdebt" title="What this answers" summary="Whether a fix earns back its cost" defaultOpen>
          <p>
            Debt has a <strong>principal</strong> (what the fix costs) and <strong>interest</strong> (time lost every
            month while it stays). A fix pays back when the interest it wins back adds up to more than it cost —
            before the code is replaced anyway.
          </p>
          <p>
            Nobody knows these numbers exactly, so each is a range. The page tries thousands of combinations and
            reports how often the fix pays back, and whether measuring the time lost first could change the call.
          </p>
        </Explainer>
        <h2 className="sr-only">Examples</h2>
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: invented numbers.</p>
      </HeroSection>

      <HeroSection>
        <SectionHead step={1} label="The fix" title="What does it cost, and what does it win back?" />
        <FieldGrid>
          <Field label="Name of the fix">
            <Input aria-label="Name of the fix" value={state.label} onChange={(e) => set({ label: e.target.value })} />
          </Field>
          <Field label="Cost of the fix" hint="Team-days, 90% range">
            <RangeInput label="Cost of the fix" min={0} value={state.principal} onChange={(principal) => set({ principal })} unit="days" />
          </Field>
          <Field label="Time lost each month now" hint="Team-days per month, 90% range">
            <RangeInput label="Time lost each month" min={0} value={state.interest} onChange={(interest) => set({ interest })} unit="days" />
          </Field>
          <Field label="Share the fix wins back" hint="Percent; few fixes win back all of it">
            <RangeInput label="Share won back" min={0} max={100} value={state.recovered} onChange={(recovered) => set({ recovered })} unit="%" />
          </Field>
          <Field label="Count savings over" hint="Months">
            <Input type="number" min={1} max={120} aria-label="Months to count savings over" value={String(state.horizonMonths)} onChange={(e) => set({ horizonMonths: Math.min(120, Math.max(1, numberOr(e.target.value, 12))) })} />
          </Field>
          <Field label="Chance the code is replaced" hint="Percent per year">
            <Input type="number" min={0} max={99} aria-label="Yearly chance the code is replaced" value={String(state.replacePct)} onChange={(e) => set({ replacePct: Math.min(99, Math.max(0, numberOr(e.target.value, 0))) })} />
          </Field>
        </FieldGrid>
        <FieldGrid className="mt-4">
          <Field label="Time lost grows by" hint="Percent per year, e.g. as the area changes more">
            <Input type="number" min={-90} max={500} aria-label="Yearly growth of time lost" value={String(state.growthPct)} onChange={(e) => set({ growthPct: Math.min(500, Math.max(-90, numberOr(e.target.value, 0))) })} />
          </Field>
        </FieldGrid>
        {!ok && (
          <Callout kind="error" className="mt-4">
            {r.issues.join(" ")}
          </Callout>
        )}
      </HeroSection>

      {ok && (
        <HeroSection>
          <SectionHead step={2} label="Payback" title="When does it earn back its cost?" />
          <MetricGrid className="lg:grid-cols-3">
            <MetricCard accent eyebrow="Chance it pays back" value={pct(r.chancePaysBack)} data-value={r.chancePaysBack}>
              Within {state.horizonMonths} months, before the code is replaced.
            </MetricCard>
            <MetricCard eyebrow="Payback, most likely" value={`${fmt(r.payback.p50)} months`}>
              90% range {fmt(r.payback.p5)} to {fmt(r.payback.p95)} months.
            </MetricCard>
            <MetricCard eyebrow="Net benefit" value={`${fmt(r.meanNet)} days`}>
              Expected; 90% range {fmt(r.net.p5)} to {fmt(r.net.p95)} team-days.
            </MetricCard>
            <MetricCard eyebrow="Time won back" value={`${fmt(r.saved.p50)} days`}>
              Over {state.horizonMonths} months; 90% range {fmt(r.saved.p5)} to {fmt(r.saved.p95)}.
            </MetricCard>
            <MetricCard eyebrow="Decision now" value={r.fix ? "Fix it" : "Leave it"}>
              {r.fix ? "On average the fix earns more than it costs." : "On average the fix costs more than it earns back."}
            </MetricCard>
            <MetricCard eyebrow="Worth measuring first" value={`${fmt(r.evpi)} days`}>
              The most that knowing every number exactly could add. Small means decide now.
            </MetricCard>
          </MetricGrid>
          <Graph
            className="mt-6"
            title="Chance the fix has paid back by each month"
            caption={`Share of simulated futures in which savings have reached the cost by each month. ${pct(r.chanceNever)} never pay back. Conditional on the ranges in 01.`}
            series={[{ id: "pb", label: "Paid back by", points: r.curve, kind: "area", tone: "accent", width: 2.5 }]}
            markers={[{ x: state.horizonMonths, label: "Horizon", tone: "ink", dashed: true }]}
            xDomain={[0, Math.max(state.horizonMonths * 1.5, 1)]}
            yDomain={[0, 1]}
            showY
            formatY={(v) => `${Math.round(v * 100)}%`}
            formatX={(v) => `${Math.round(v)}`}
            xLabel="Months"
          />
          <BriefLines className="mt-6" lines={summaryLines(r, state)} />
          <OpenInTool
            title="Plan it"
            targets={[
              {
                id: "sprintmix",
                name: "Sprint Mix",
                use: hub
                  ? "Return to your sprint plan with this fix added as an Improve item for the build team."
                  : "Plan a sprint with this fix as an Improve item for the build team, beside features and experiments.",
                href: mixHref,
                reason: "Your sprint plan sizes work in points; this fix is in team-days. Switch the plan to days first.",
              },
            ]}
            note="The fix's name, its cost range and the 90% range of time won back travel, valued in team-days."
          />
        </HeroSection>
      )}
    </HeroResultLayout>
  );
}
